import { useEffect, useState } from "react";
import { api, eur } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Percent, Info, TrendingUp, TrendingDown } from "lucide-react";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);
const pct = (v) => (v === null || v === undefined ? "n.v.t." : `${v.toFixed(2).replace(".", ",")} %`);

function MargeCell({ v }) {
  if (v === null || v === undefined) return <span className="text-muted-foreground">n.v.t.</span>;
  const up = v >= 0;
  return (
    <span className={`inline-flex items-center gap-1 font-semibold ${up ? "text-emerald-700" : "text-red-600"}`}>
      {up ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
      {pct(v)}
    </span>
  );
}

export default function RapportenMarge() {
  const [from, setFrom] = useState(firstOfYear());
  const [to, setTo] = useState(today());
  const [locations, setLocations] = useState([]);
  const [loc, setLoc] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => { api.get("/locations").then((r) => setLocations(r.data)).catch(() => setLocations([])); }, []);

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/marge", { params: { start: from, end: to, location_id: loc } })
      .then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const rows = data?.rows || [];
  const totals = data?.totals || { sales: 0, purchases: 0, gross_margin: 0, margin_pct: null };

  return (
    <div className="space-y-6" data-testid="rapporten-marge-page">
      <div>
        <p className="text-xs font-semibold tracking-widest text-violet-700 uppercase">Verkopen (dagboek) vs aankopen (handelsgoederen)</p>
        <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
          <Percent className="h-7 w-7 text-violet-600" /> Marge per type
        </h1>
      </div>

      <div className="flex items-start gap-2 text-xs text-muted-foreground bg-violet-50/60 border border-violet-100 rounded-md p-3">
        <Info className="h-4 w-4 mt-0.5 text-violet-600 shrink-0" />
        Marge % = (verkopen excl. btw / aankopen excl. btw) × 100 − 100. Cijfers komen rechtstreeks uit
        het <b>grootboek</b> (bevestigde boekingen): verkopen uit rekeningen 700xxx, aankopen handelsgoederen
        uit 604xxx per tarief (creditnota's automatisch verrekend). Zo komen ze 1-op-1 overeen met de resultatenrekening.
      </div>

      <Card className="shadow-sm">
        <CardContent className="p-4 flex flex-wrap items-end gap-4">
          <div className="space-y-1.5"><Label>Van</Label><Input type="date" data-testid="marge-from" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div className="space-y-1.5"><Label>Tot</Label><Input type="date" data-testid="marge-to" value={to} onChange={(e) => setTo(e.target.value)} /></div>
          <div className="space-y-1.5">
            <Label>Vestiging</Label>
            <div className="h-10 flex items-center rounded-md border border-input bg-muted px-3 text-sm text-muted-foreground" data-testid="marge-location-note">
              Grootboek (alle vestigingen)
            </div>
          </div>
          <Button className="bg-violet-600 hover:bg-violet-700" data-testid="marge-refresh" onClick={load}>Tonen</Button>
        </CardContent>
      </Card>

      {loading ? <div className="py-16 text-center text-muted-foreground">Laden…</div> : !data ? null : (
        <Card className="shadow-sm">
          <CardHeader className="pb-2"><CardTitle className="font-heading text-lg">Marge per btw-type</CardTitle></CardHeader>
          <CardContent className="p-0">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Type</TableHead>
                <TableHead className="text-right">Verkopen (excl.)</TableHead>
                <TableHead className="text-right">Aankopen (excl.)</TableHead>
                <TableHead className="text-right">Brutomarge</TableHead>
                <TableHead className="text-right">Marge %</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.type} data-testid={`marge-row-${r.type.replace(/[^a-zA-Z0-9]/g, "-")}`}>
                    <TableCell className="font-medium">{r.type}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(r.sales)}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(r.purchases)}</TableCell>
                    <TableCell className="text-right tabular-nums">{eur(r.gross_margin)}</TableCell>
                    <TableCell className="text-right"><MargeCell v={r.margin_pct} /></TableCell>
                  </TableRow>
                ))}
                {data.lotto && (
                  <TableRow data-testid="marge-row-lotto" className="text-muted-foreground">
                    <TableCell className="font-medium">{data.lotto.type}
                      <span className="ml-2 text-[11px] italic">({data.lotto.note})</span>
                    </TableCell>
                    <TableCell className="text-right tabular-nums">{eur(data.lotto.sales)}</TableCell>
                    <TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right">—</TableCell>
                    <TableCell className="text-right">n.v.t.</TableCell>
                  </TableRow>
                )}
                <TableRow className="bg-muted/40 font-semibold">
                  <TableCell>Totaal (handelsgoederen)</TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="marge-total-sales">{eur(totals.sales)}</TableCell>
                  <TableCell className="text-right tabular-nums" data-testid="marge-total-purchases">{eur(totals.purchases)}</TableCell>
                  <TableCell className="text-right tabular-nums">{eur(totals.gross_margin)}</TableCell>
                  <TableCell className="text-right" data-testid="marge-total-pct"><MargeCell v={totals.margin_pct} /></TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
